'use client';

import { LinkProps as NextLinkProps } from 'next/link';

import { trackClick } from '@sola/cookies/components/datalayer.jsx';
import { getTextFromReactNode } from '@sola/utils/react.js';

import { useRouter } from '../../utils/hooks/useRouter';

export type LinkProps = NextLinkProps &
  React.PropsWithChildren & {
    id?: string;
    className?: string;
    isDisabled?: boolean;
    title?: string;
    download?: boolean;
    type?: 'button' | 'submit' | 'reset';
    target?: string;
    rel?: string;
  };
export const Link: React.FC<LinkProps> = ({ isDisabled, ...props }) => {
  const { RouterComponent, usePathname } = useRouter();

  const pathname = usePathname();

  const linkProps = {
    ...props,
    ...(pathname === props.href && {
      'aria-current': true,
    }),
  };

  return !RouterComponent || isDisabled ? (
    // Disabling this rule because the it doesn't apply when explictly setting role="link"
    // @see https://www.scottohara.me/blog/2021/05/28/disabled-links.html
    // @see https://w3c.github.io/html-aria/#example-communicate-a-disabled-link-with-aria
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      {...(linkProps as React.ComponentProps<'a'>)}
      {...(isDisabled && {
        'aria-disabled': true,
        role: 'link',
        href: undefined,
      })}
    />
  ) : (
    <RouterComponent
      {...linkProps}
      onClick={(e) => {
        trackClick(
          linkProps.title ||
            getTextFromReactNode(linkProps.children) ||
            'Unknown',
          linkProps.href?.toString(),
          linkProps.className,
          linkProps.id,
        );
        if (linkProps.onClick) {
          linkProps.onClick(e);
        }
      }}
    />
  );
};
